import {FC, useState, useEffect, useCallback, Key} from 'react';
import {styled} from 'styled-components';
import {useParams} from 'wouter';

import {asNumberOrThrow} from '@shared/lib/utils/type_utils';
import {FrontendAccountNotification} from '@shared/models/api_models';

import {Button, ButtonAsLink} from '@shared_web/components/core/button';
import {SvgIcon} from '@shared_web/components/core/svg_icon';
import {Flex} from '@shared_web/components/core2/flex';
import {icon_circle_fill} from '@shared_web/components/icons/icon_circle_fill';
import {notifyError} from '@shared_web/lib/notification';

import {AlertSymbol} from '@mavismart_frontend/components/alert_symbol';
import {DateText} from '@mavismart_frontend/components/date_text';
import {Loader} from '@mavismart_frontend/components/loader';
import {findTranslationKey, Translate} from '@mavismart_frontend/components/translate';
import {apiCall} from '@mavismart_frontend/lib/api';
import {BUTTON_THEME, LINK_THEME, MAIN_COLOR} from '@mavismart_frontend/lib/constants';
import {GenericCell} from '@mavismart_frontend/pages/cells/generic_cell';
import {WrapperLoadMore} from '@mavismart_frontend/pages/tabs/door_detail_message';
import {setHasUnreadNotifications} from '@mavismart_frontend/stores/notifications_store';

export const NotificationsList: FC = () => {
  const {notificationId} = useParams<{notificationId?: string}>();
  const [allNotifications, setAllNotifications] = useState<
    FrontendAccountNotification[] | undefined
  >();
  const [nextPaginationToken, setNextPaginationToken] = useState<string | undefined>(undefined);

  const listAccountNotifications = useCallback(async (paginationToken?: string) => {
    try {
      const response = await apiCall('POST /list-account-notifications', {
        limit: 10,
        paginationToken,
      });
      setAllNotifications(prev =>
        [...(prev ?? []), ...response.notifications].sort((a, b) => b.ts - a.ts)
      );
      setNextPaginationToken(response.nextPaginationToken);
    } catch (error) {
      notifyError(error);
    }
  }, []);

  useEffect(() => {
    listAccountNotifications().catch(notifyError);
  }, [listAccountNotifications]);

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await apiCall('POST /read-all-account-notifications', {});
      setAllNotifications(prev => prev?.map(notification => ({...notification, status: 'read'})));
      setHasUnreadNotifications([]);
    } catch (error) {
      notifyError(error);
    }
  }, []);

  const handleShowMore = useCallback(async () => {
    if (nextPaginationToken === undefined) {
      return;
    }
    await listAccountNotifications(nextPaginationToken);
  }, [nextPaginationToken, listAccountNotifications]);

  const handleClick = useCallback(
    async (key: Key) => {
      const ts = asNumberOrThrow(key);
      const notification = allNotifications?.find(n => n.ts === ts);
      if (notification === undefined || notification.status === 'read') {
        return;
      }
      try {
        await apiCall('POST /update-account-notification-status', {
          newStatus: 'read',
          ts,
        });
        setAllNotifications(prev => prev?.map(n => (n.ts === ts ? {...n, status: 'read'} : n)));
        if (allNotifications?.some(n => n.status === 'unread' && n.ts !== ts) !== true) {
          setHasUnreadNotifications([]);
        }
      } catch (error) {
        notifyError(error);
      }
    },
    [allNotifications]
  );

  if (!allNotifications) {
    return <Loader />;
  }

  return (
    <>
      {allNotifications.length === 0 ? (
        <>
          <Translate value="no_notifications" />
        </>
      ) : (
        <NotificationsPageWrapper>
          <Content>
            <NotificationListWrapper>
              {allNotifications.map(notification => {
                const translateKey = findTranslationKey(notification.doorMessage?.category);
                return (
                  <GenericCell
                    key={notification.ts}
                    id={notification.ts}
                    icon={<AlertSymbol level={notification.doorMessage?.level ?? 'green'} />}
                    title={
                      <>
                        {translateKey ? (
                          <Translate value={translateKey} />
                        ) : (
                          <>{notification.doorMessage?.category}</>
                        )}
                      </>
                    }
                    // subtitle={
                    //   <>
                    //     {notification.doorId && <>{notification.doorId} - </>}
                    //     <DateText date={new Date(notification.ts)} />
                    //   </>
                    // }
                    subtitle={
                      <Flex $column>
                        {notification.doorId && <div>{notification.doorId}</div>}
                        <div>
                          <DateText date={new Date(notification.ts)} />
                        </div>
                      </Flex>
                    }
                    status={notification.status as 'read' | 'unread'}
                    rightIcon={
                      <SvgIcon icon={icon_circle_fill} size={10} color={`${MAIN_COLOR}`} />
                    }
                    to={`/user/notifications/${notification.ts}/messages/${notification.doorMessage?.category ?? 'unknown'}`}
                    onClick={handleClick}
                    highlighted={String(notification.ts) === notificationId}
                  />
                );
              })}
            </NotificationListWrapper>
            {nextPaginationToken && (
              <WrapperLoadMore>
                <ButtonAsLink onClickAsync={handleShowMore} themeOverrides={LINK_THEME}>
                  <Translate value="load_more" />
                </ButtonAsLink>
              </WrapperLoadMore>
            )}
          </Content>
          <WrapperButtons>
            <Button themeOverrides={BUTTON_THEME} onClickAsync={handleMarkAllAsRead}>
              <Translate value="mark_all_as_read" />
            </Button>
          </WrapperButtons>
        </NotificationsPageWrapper>
      )}
    </>
  );
};

NotificationsList.displayName = 'NotificationsList';

const NotificationsPageWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-left: -10px;
  width: calc(100% + 20px);
`;

const WrapperButtons = styled.div`
  width: 100%;
  height: 58px;
  flex-shrink: 0;
  
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 0;

  background: white;
`;

const Content = styled.div`
  height: 0;
  flex-grow: 1;
  overflow-y: auto;
`;

const NotificationListWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
